import { useState } from 'react';
import type { PublicUser } from '@boardflight/shared';
import { api, post } from './api';
import { NoticeBanner } from './kit/feedback';
import { describe, useAsync } from './ui';

/**
 * Asks an account that has not proven its address to open the link signup sent.
 *
 * Read from `/v1/me` rather than from the stored session, so it disappears the moment the link has
 * been opened — in this tab after the confirm page's Continue, or in any other on its next load.
 * Joining a team, receiving a game by email and starting a playground all wait for it, and each of
 * those refusals points back here.
 */
export function ConfirmEmailBanner() {
  const me = useAsync(() => api<{ user: PublicUser }>('/v1/me'), []);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const user = me.data?.user;
  if (!user || user.emailVerified) return null;

  async function resend() {
    setBusy(true);
    setError(null);
    try {
      const result = await post<{ verified: boolean }>('/v1/auth/email/verify/request', {});
      if (result.verified) me.reload();
      else setSent(true);
    } catch (failure) {
      setError(describe(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bf-shell bf-shell-notice">
      <NoticeBanner
        tone="warning"
        title="Confirm your email address"
        detail={error ?? undefined}
        actionLabel={sent ? undefined : busy ? 'Sending…' : 'Send it again'}
        onAction={busy ? undefined : () => void resend()}
      >
        {sent
          ? `A new link is on its way to ${user.email}. It works once and expires in a day.`
          : `We sent a link to ${user.email}. Open it to join teams you are invited to and start a playground.`}
      </NoticeBanner>
    </div>
  );
}
